import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string;
}

/**
 * Last-resort boundary at the app root. If any render throw escapes the
 * feature-level boundaries, this catches it and shows a recoverable
 * full-screen message instead of leaving the user on a blank white page.
 *
 * Deliberately self-contained: the fallback uses inline styles and no app
 * stores/contexts, so it still renders even when the thing that threw is CSS,
 * a provider, or global state.
 */
export class AppErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null, componentStack: '' };

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: '' };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[AppErrorBoundary] Unhandled render error:', error, info.componentStack);
    // Keep the component stack so it can be shown on-screen — a screenshot of
    // the fallback is then enough to pinpoint where the throw came from.
    this.setState({ componentStack: info.componentStack ?? '' });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleCopy = () => {
    const { error, componentStack } = this.state;
    const text = [
      `${error?.name ?? 'Error'}: ${error?.message ?? 'unknown error'}`,
      error?.stack ?? '',
      '--- component stack ---',
      componentStack,
    ].join('\n');
    navigator.clipboard?.writeText(text).catch(() => { /* clipboard unavailable */ });
  };

  override render() {
    if (!this.state.error) return this.props.children;

    const { error, componentStack } = this.state;
    // Full, screenshot-friendly detail: name + message, then the JS stack and
    // the React component stack. Trim each so a deep stack still fits a phone
    // screen without scrolling the most useful (top) frames off the top.
    const details = [
      `${error.name}: ${error.message}`,
      error.stack
        ? '\n' + error.stack.split('\n').slice(0, 8).join('\n')
        : '',
      componentStack
        ? '\nComponent stack:' + componentStack.split('\n').slice(0, 8).join('\n')
        : '',
    ].join('\n').trim();

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: 24,
          textAlign: 'center',
          background: '#18181b',
          color: '#e4e4e7',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          zIndex: 2147483647,
          overflow: 'auto',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>Something went wrong</div>
        <div style={{ fontSize: 13, color: '#a1a1aa', maxWidth: 360 }}>
          The app hit an unexpected error. Reloading usually fixes it — if it
          keeps happening, screenshot the details below and send them over.
        </div>
        <pre
          style={{
            margin: 0,
            padding: '10px 12px',
            maxWidth: 'min(560px, 92vw)',
            maxHeight: '45vh',
            overflow: 'auto',
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 11,
            lineHeight: 1.45,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            color: '#fca5a5',
            background: '#27272a',
            border: '1px solid #3f3f46',
            borderRadius: 8,
            userSelect: 'text',
          }}
        >
          {details}
        </pre>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={this.handleReload}
            style={{
              padding: '8px 20px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: '#3f3f46',
              border: '1px solid #52525b',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <button
            onClick={this.handleCopy}
            style={{
              padding: '8px 20px',
              fontSize: 13,
              fontWeight: 600,
              color: '#e4e4e7',
              background: 'transparent',
              border: '1px solid #52525b',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Copy details
          </button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
